import React, { memo, useRef, useState, useLayoutEffect, useMemo } from 'react';
import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react';
import { NODE_TYPE_CONFIG, FIELD_TYPE_ABBR } from './erdConstants';

function ERDNode({ id, type, data, selected }) {
  const nodeType = type ?? 'collection';
  const cfg = NODE_TYPE_CONFIG[nodeType] ?? NODE_TYPE_CONFIG.collection;
  const isNote = nodeType === 'note';
  const showFieldHandles = !!data.showFieldHandles && !isNote;
  const fields = useMemo(() => data.fields ?? [], [data.fields]);

  const nodeRef = useRef(null);
  const [fieldTops, setFieldTops] = useState({});
  const updateNodeInternals = useUpdateNodeInternals();

  // Measure actual field row positions after render using offsetTop
  useLayoutEffect(() => {
    if (!showFieldHandles || !nodeRef.current) {
      setFieldTops({});
      return;
    }
    const tops = {};
    nodeRef.current.querySelectorAll('[data-field-handle-id]').forEach(el => {
      const containerTop = nodeRef.current.offsetTop;
      const top = el.offsetTop - containerTop + el.offsetHeight / 2;
      tops[el.dataset.fieldHandleId] = top;
    });
    setFieldTops(tops);
    // Tell React Flow to recompute handle positions now that we've added/moved them
    updateNodeInternals(id);
  }, [id, showFieldHandles, fields, data.description, updateNodeInternals]);

  const handleStyle = { background: cfg.border, border: '2px solid #0f172a' };
  const fieldHandleStyle = {
    background: cfg.border,
    border: '2px solid #0f172a',
    width: 7,
    height: 7,
  };

  return (
    <div
      ref={nodeRef}
      style={{
        border: `1.5px solid ${selected ? '#f1f5f9' : cfg.border}`,
        borderRadius: 8,
        minWidth: 240,
        maxWidth: 320,
        backgroundColor: cfg.bodyBg,
        boxShadow: selected ? `0 0 0 2px ${cfg.border}` : '0 2px 8px rgba(0,0,0,0.4)',
        transition: 'box-shadow 0.15s ease',
      }}
    >
      {/* Center handles — always show T/B, show L/R only when field handles are off */}
      {!showFieldHandles && (
        <>
          <Handle type="target" position={Position.Left}  id="l" style={{ ...handleStyle, top: '50%' }} />
          <Handle type="source" position={Position.Right} id="r" style={{ ...handleStyle, top: '50%' }} />
        </>
      )}
      <Handle type="target" position={Position.Top}    id="t" style={{ ...handleStyle, left: '50%' }} />
      <Handle type="source" position={Position.Bottom} id="b" style={{ ...handleStyle, left: '50%' }} />

      {/* Per-field handles */}
      {showFieldHandles && fields.map(f => {
        const top = fieldTops[f.id];
        if (top === undefined) return null;
        return (
          <React.Fragment key={`fh-${f.id}`}>
            <Handle
              type="target"
              position={Position.Left}
              id={`field-${f.id}-l`}
              className="erd-field-handle"
              style={{ ...fieldHandleStyle, top }}
            />
            <Handle
              type="source"
              position={Position.Right}
              id={`field-${f.id}-r`}
              className="erd-field-handle"
              style={{ ...fieldHandleStyle, top }}
            />
          </React.Fragment>
        );
      })}

      {/* Header */}
      <div className="erd-node-header" style={{ backgroundColor: cfg.headerBg }}>
        <span className="erd-node-type-badge" style={{ color: cfg.textColor }}>{cfg.label}</span>
        <span className="erd-node-label">{data.label}</span>
      </div>

      {/* Description */}
      {data.description && (
        <div className="erd-node-description">{data.description}</div>
      )}

      {isNote ? (
        <div className="erd-node-note-body">
          {data.description || <span className="erd-node-placeholder">Double-click to edit</span>}
        </div>
      ) : (
        <>
          {fields.length > 0 && (
            <div className="erd-node-fields">
              {fields.map(f => (
                <div
                  key={f.id}
                  className={`erd-node-field ${showFieldHandles ? 'has-handles' : ''}`}
                  data-field-handle-id={f.id}
                >
                  <span className="erd-node-field-indicator">
                    {f.isDocId ? '🔑' : f.required ? '●' : '○'}
                  </span>
                  <span className="erd-node-field-name" title={f.description || ''}>{f.name}</span>
                  <span className="erd-node-field-type" style={{ backgroundColor: cfg.headerBg + '66', color: cfg.textColor }}>
                    {FIELD_TYPE_ABBR[f.type] ?? f.type}
                  </span>
                </div>
              ))}
            </div>
          )}

          {data.subcollections && data.subcollections.length > 0 && (
            <div className="erd-node-subcollections" style={{ borderColor: cfg.border + '44' }}>
              {data.subcollections.map((sub, i) => (
                <div key={i} className="erd-node-subcollection-item">
                  <span>📁</span>
                  <span className="erd-node-subcollection-name">{sub}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default memo(ERDNode);
